import {
  executeWorkflowAnalysis,
  throwWorkflowValidationError,
} from "../../../../utils/util";

// Form validation for product add / update
export const workflowValidation = (data) => {
  if (!data?.name || !data?.description) {
    return false;
  } else {
    return true;
  }
};

// 1. Milestone Connections Validation
export async function validateMilestoneConnections(graph) {
  const { nodes, edges } = graph;
  const errors = [];
  const milestoneNodes = nodes?.filter((n) => n?.data?.taskData?.isMilestone);
  if (milestoneNodes?.length === 0) {
    return {
      isValid: false,
      errors: ["No milestone nodes found in the graph"],
    };
  }

  const incomingConnections = new Map();
  const outgoingConnections = new Map();
  milestoneNodes.forEach((node) => {
    incomingConnections.set(node.id, 0);
    outgoingConnections.set(node.id, 0);
  });

  edges.forEach((edge) => {
    if (
      incomingConnections.has(edge.source) &&
      incomingConnections.has(edge.target)
    ) {
      outgoingConnections.set(
        edge.source,
        outgoingConnections.get(edge.source) + 1
      );
      incomingConnections.set(
        edge.target,
        incomingConnections.get(edge.target) + 1
      );
    }
  });

  let startNodesCount = 0;
  let endNodesCount = 0;

  milestoneNodes?.forEach((node) => {
    const incoming = incomingConnections.get(node.id);
    const outgoing = outgoingConnections.get(node.id);
    const milestoneName = node.data.taskData.name;

    if (incoming === 0 && outgoing === 0) {
      errors.push(
        `Milestone "${milestoneName}" (ID: ${node.id}) is isolated with no connections`
      );
    }
    if (incoming === 0 && outgoing > 0) {
      startNodesCount++;
    }
    if (incoming > 0 && outgoing === 0) {
      endNodesCount++;
    }
    if (incoming > 1) {
      errors.push(
        `Milestone "${milestoneName}" (ID: ${node.id}) has multiple incoming connections (${incoming})`
      );
    }
    if (outgoing > 1) {
      errors.push(
        `Milestone "${milestoneName}" (ID: ${node.id}) has multiple outgoing connections (${outgoing})`
      );
    }
  });

  if (startNodesCount === 0) {
    errors.push(
      "No start milestone found (milestone with no incoming connections)"
    );
  } else if (startNodesCount > 1) {
    errors.push(`Multiple start milestones found (${startNodesCount})`);
  }

  if (endNodesCount === 0) {
    errors.push(
      "No end milestone found (milestone with no outgoing connections)"
    );
  } else if (endNodesCount > 1) {
    errors.push(`Multiple end milestones found (${endNodesCount})`);
  }
  // console.log("error", errors);
  return {
    isValid: errors.length === 0,
    errors,
  };
}

// 2. Check for exact milestone sequence
export async function isExactSequencePresent(
  sortedBySequence,
  milestonSequenceBaseImpSeq
) {
  const errors = [];
  for (
    let i = 0;
    i <= sortedBySequence?.length - milestonSequenceBaseImpSeq?.length;
    i++
  ) {
    const slice = sortedBySequence.slice(
      i,
      i + milestonSequenceBaseImpSeq?.length
    );
    if (slice.join(",") === milestonSequenceBaseImpSeq?.join(",")) {
      return true;
    }
  }
  return {
    isValid: false,
    errors: ["Please  maintain sequence of milestones"],
  };
}
// 3. Validate Start Node Structure
export async function validateStartNodeStructure(workflows) {
  let startNodeCount = 0;
  let errObj = {};
  let errorTaskId = [];
  for (const workflow of workflows) {
    const isMainStart = workflow?.isStartNode === true;
    const subTasks = workflow?.subTasks || [];
    const subTasksStartCount = subTasks?.filter(
      (st) => st?.isStartNode === true
    )?.length;

    if (isMainStart) {
      startNodeCount++;
      errorTaskId.push(workflow.id);
      const invalidSubTasks = subTasks.some((st) => st.isStartNode !== true);
      if (invalidSubTasks) {
        errObj.errorTaskId = errorTaskId;
        errObj.error =
          "All subtasks must have isStartNode: true if main task isStartNode.";
        return errObj;
      }
    } else {
      if (subTasksStartCount > 0) {
        errObj.errorTaskId = errorTaskId;
        errObj.error =
          "Subtasks have isStartNode: true but main task is not a start node.";
        return errObj;
      }
    }
  }

  if (startNodeCount !== 1) {
    errObj.errorTaskId = errorTaskId;
    errObj.error =
      "At least one start node is required in the entire workflow.";
    return errObj;
  }

  return errObj;
}

// 4. Validate Direct Submission Node Structure
export async function validateDirectSubmissionNodeStructure(workflows = []) {
  let directSubmissionNodeCount = 0;
  let errObj = {};
  let errorTaskId = [];

  for (const workflow of workflows) {
    const isApplicableOnDirectSubmmissionCheck =
      workflow?.isApplicableOnDirectSubmmission === true;
    const subTasks = workflow?.subTasks || [];
    const subTasksStartCount = subTasks?.filter(
      (st) => st?.isApplicableOnDirectSubmmission === true
    )?.length;

    if (isApplicableOnDirectSubmmissionCheck) {
      directSubmissionNodeCount++;
      errorTaskId.push(workflow.id);
      const invalidSubTasks = subTasks.some(
        (st) => st.isApplicableOnDirectSubmmission !== true
      );
      if (invalidSubTasks) {
        errObj.errorTaskId = errorTaskId;
        errObj.error =
          "All subtasks must have isApplicableOnDirectSubmmission: true if main task has Direct Submmission.";
        return errObj;
      }

      if (workflow.milestone !== "Instructed") {
        errObj.errorTaskId = errorTaskId;
        errObj.error =
          "The direct instructed start node must be assigned to a task within an instructed milestone.";
        return errObj;
      }
      if (workflow?.isGeneric === false && workflow?.productId) {
        errObj.errorTaskId = errorTaskId;
        errObj.error = `Direct-instructed start node task: ${workflow.name} must be kept generic and should not have a product associated with it.`;
        return errObj;
      }
    } else {
      if (subTasksStartCount > 0) {
        errObj.errorTaskId = errorTaskId;
        errObj.error =
          "Subtasks have isApplicableOnDirectSubmmission: true but main task  has Direct Submmission..";
        return errObj;
      }
    }
  }

  if (directSubmissionNodeCount !== 1) {
    errObj.errorTaskId = errorTaskId;
    errObj.error =
      "The workflow must include at least one direct instructed start node.";
    return errObj;
  }

  return errObj;
}

// 5. Validate Before Instructed Structure
export async function validateBeforeInstructedStructure(workflows = []) {
  let errObj = {};
  let errorTaskId = [];

  for (const workflow of workflows) {
    const subTasks = workflow?.subTasks || [];

    if (
      workflow.milestone === "Prospect" ||
      workflow.milestone === "Not Instructed"
    ) {
      if (workflow?.isGeneric === false && workflow?.productId) {
        errorTaskId.push(workflow.id);
        errObj.error = `No product associated to task before milestone Instructed. Please check: ${workflow.name}`;
        errObj.errorTaskId = errorTaskId;
        return errObj;
      }

      for (const subTask of subTasks) {
        if (subTask?.isGeneric === false && subTask?.productId) {
          errorTaskId.push(workflow.id);
          errObj.error = `No product associated to sub task before milestone Instructed: ${subTask.name}`;
          errObj.errorTaskId = errorTaskId;
          return errObj;
        }
      }
    }
  }

  return errObj;
}

// 6. Detect Invalid Self-Referencing Tasks
export async function detectInvalidSelfReferencingTasks(workflows) {
  const invalidTasks = [];

  function checkOutcomes(nodeId, nodeName, outcomes) {
    if (!outcomes || outcomes.length === 0) return;

    const allOutcomesIncludeSelfOrSelfPlusOthers = outcomes.every((outcome) => {
      const triggers = outcome.TriggerTasks ?? [];
      return (
        triggers.includes(nodeId) &&
        (triggers.length === 1 || triggers.length > 1)
      );
    });

    if (allOutcomesIncludeSelfOrSelfPlusOthers) {
      const offendingOutcome = outcomes.find((o) =>
        (o.TriggerTasks ?? []).includes(nodeId)
      );

      if (offendingOutcome) {
        invalidTasks.push({
          taskId: nodeId,
          taskName: nodeName,
          outcomeName: offendingOutcome.name,
          reason: "All outcomes reference self and no outward path exists.",
        });
      }
    }
  }

  (workflows ?? []).forEach((workflow) => {
    const taskId = workflow.id;
    const taskName = workflow.name;

    checkOutcomes(taskId, taskName, workflow.outcomes);

    (workflow.subTasks ?? []).forEach((subtask) => {
      if (subtask.id !== undefined) {
        checkOutcomes(
          subtask.id,
          subtask.name ?? "Unnamed Subtask",
          subtask.outcomes
        );
      }
    });
  });

  return {
    hasInvalidReferences: invalidTasks.length > 0,
    invalidTasks,
  };
}

// 7. Analyze Workflow Connections (flat)
export async function analyzeWorkflowConnections(workflows = []) {
  const allTasks = [];
  const allTriggerIds = new Set();

  workflows.forEach((wf) => {
    if (wf.outcomes) {
      wf.outcomes.forEach((outcome) => {
        outcome.TriggerTasks?.forEach((tid) => allTriggerIds.add(tid));
      });
    }

    if (wf.subTasks) {
      wf.subTasks.forEach((sub) => {
        if (sub.outcomes) {
          sub.outcomes.forEach((outcome) => {
            outcome.TriggerTasks?.forEach((tid) => allTriggerIds.add(tid));
          });
        }
        allTasks.push({ ...sub, isSubTask: true });
      });
    }

    allTasks.push({ ...wf, isSubTask: false });
  });

  const taskMap = {};
  allTasks.forEach((task) => {
    taskMap[task.id] = task;
  });

  const enrichedTasks = allTasks.map((task) => {
    const outcomes = task.outcomes || [];
    const allTriggerTasks = outcomes.flatMap((o) => o.TriggerTasks || []);
    const triggersAnother = allTriggerTasks.length > 0;
    const triggersOnlyGrounded = allTriggerTasks.length === 0;
    const isReferencedByOthers = allTriggerIds.has(task.id);

    const isNoConnection = !isReferencedByOthers;
    const isDeadEnd = outcomes.length > 0 && triggersOnlyGrounded;
    const isSinkNode = isDeadEnd && isReferencedByOthers;

    return {
      ...task,
      isNoConnection,
      isDeadEnd,
      isSinkNode,
    };
  });

  return enrichedTasks;
}

// 8. Analyze Workflow Connections (safe, reconstructs structure)
export async function analyzeWorkflowConnections2(workflows = []) {
  try {
    if (!Array.isArray(workflows)) {
      console.warn("Invalid input: workflows must be an array");
      return [];
    }

    const allTasks = [];
    const allTriggerIds = new Set();

    const processOutcomes = (outcomes = []) => {
      if (!Array.isArray(outcomes)) return;
      outcomes.forEach((outcome) => {
        const triggerTasks = outcome?.TriggerTasks ?? [];
        triggerTasks.forEach((tid) => {
          if (tid != null) allTriggerIds.add(tid);
        });
      });
    };

    const safeClone = (obj) => {
      try {
        return obj ? { ...obj } : {};
      } catch (error) {
        console.warn("Clone failed:", error);
        return {};
      }
    };

    workflows.forEach((wf) => {
      if (!wf) return;
      processOutcomes(wf?.outcomes);

      const subTasks = wf?.subTasks ?? [];
      if (Array.isArray(subTasks)) {
        subTasks.forEach((sub) => {
          if (!sub) return;
          processOutcomes(sub?.outcomes);
          allTasks.push({
            ...safeClone(sub),
            id: sub?.id ?? null,
            name: sub?.name ?? "",
            isSubTask: true,
            mainTaskId: sub?.mainTaskId ?? wf?.id ?? null,
            outcomes: sub?.outcomes ?? [],
          });
        });
      }

      allTasks.push({
        ...safeClone(wf),
        id: wf?.id ?? null,
        name: wf?.name ?? "",
        isSubTask: false,
        outcomes: wf?.outcomes ?? [],
      });
    });

    const taskMap = new Map();
    allTasks.forEach((task) => {
      if (task?.id != null) {
        taskMap.set(task.id, task);
      }
    });

    const enrichedTasks = allTasks
      .map((task) => {
        if (!task) return null;
        const outcomes = task?.outcomes ?? [];
        const allTriggerTasks = outcomes
          .flatMap((o) => o?.TriggerTasks ?? [])
          .filter((id) => id != null);

        const taskId = task?.id;
        return {
          ...safeClone(task),
          isNoConnection: taskId != null ? !allTriggerIds.has(taskId) : true,
          isDeadEnd: outcomes.length > 0 && allTriggerTasks.length === 0,
          isSinkNode:
            outcomes.length > 0 &&
            allTriggerTasks.length === 0 &&
            taskId != null &&
            allTriggerIds.has(taskId),
        };
      })
      .filter(Boolean);

    const reconstructedWorkflows = workflows
      .map((wf) => {
        if (!wf) return null;
        const enrichedWf =
          enrichedTasks.find((t) => t?.id === wf?.id) ?? safeClone(wf);

        const enrichedSubTasks = Array.isArray(wf?.subTasks)
          ? wf.subTasks
              .map((sub) => {
                if (!sub) return null;
                return (
                  enrichedTasks.find(
                    (t) => t?.id === sub?.id && t?.mainTaskId === wf?.id
                  ) ?? safeClone(sub)
                );
              })
              .filter(Boolean)
          : undefined;

        return {
          ...safeClone(enrichedWf),
          subTasks: enrichedSubTasks,
          id: enrichedWf?.id ?? null,
          name: enrichedWf?.name ?? "",
          outcomes: enrichedWf?.outcomes ?? [],
          actor: enrichedWf?.actor ?? "",
          instructionTypeId: enrichedWf?.instructionTypeId ?? null,
          slaTypeLookupId: enrichedWf?.slaTypeLookupId ?? null,
        };
      })
      .filter(Boolean);

    return reconstructedWorkflows;
  } catch (error) {
    console.error("Error in analyzeWorkflowConnections2:", error);
    return [];
  }
}

// 9. Milestone Sequence Extraction
export async function getMilestoneSequence(graph) {
  const { nodes, edges } = graph;
  const milestones = new Map();
  nodes.forEach((n) => {
    const td = n?.data?.taskData;
    if (td?.isMilestone) {
      milestones.set(n.id, {
        nodeId: n.id,
        id: td.id,
        name: td.name,
      });
    }
  });

  if (milestones.size === 0) return [];

  const out = {};
  const indeg = {};
  milestones.forEach((_, id) => {
    out[id] = [];
    indeg[id] = 0;
  });

  edges.forEach((e) => {
    if (milestones.has(e.source) && milestones.has(e.target)) {
      out[e.source].push(e.target);
      indeg[e.target] += 1;
    }
  });

  const startId = Object.keys(indeg).find((k) => indeg[k] === 0);
  if (startId === undefined)
    throwWorkflowValidationError("No start milestone found");

  const sequence = [];
  const visited = new Set();
  let current = startId;
  let sequenceId = 1;

  while (current && !visited.has(current)) {
    visited.add(current);
    const milestone = milestones.get(current);
    sequence.push({
      id: milestone.id,
      nodeId: milestone.nodeId,
      name: milestone.name,
      sequenceId: sequenceId,
      y: nodes.find((n) => n.id === milestone.nodeId)?.position?.y,
    });
    current = out[current][0];
    sequenceId++;
  }

  return sequence;
}

// 10. Validate Unique Task Names
// export async function validateUniqueTaskNames(tasks = []) {
//   const nameMap = new Map();
//   let errorMsg;
//   let errorTaskId = [];
//   let errorSubTaskId = [];
//   const taskList = [];

//   tasks.forEach((main, i) => {
//     taskList.push({
//       name: main?.name,
//       where: `main task at index ${i}`,
//       id: main.id,
//       subid: null,
//     });

//     main?.subTasks?.forEach((sub, j) => {
//       taskList.push({
//         name: sub?.name,
//         where: `subtask at index ${j} under '${main?.name ?? "Unnamed"}'`,
//         id: main.id,
//         subid: sub.id,
//       });
//     });
//   });

//   for (const { name, where, id, subid } of taskList) {
//     if (!name) continue;
//     const normalized = name.trim().toLowerCase();
//     if (nameMap.has(normalized)) {
//       errorMsg = `Duplicate task name '${name}' found in ${where}. Also used in ${nameMap.get(
//         normalized
//       )}.`;
//       errorTaskId.push(id);
//       errorSubTaskId.push({
//         parentId: id,
//         subtaskId: subid,
//       });
//       return {
//         isValid: false,
//         message: errorMsg,
//         details: {
//           errorTaskId: errorTaskId,
//           errorSubTaskId: errorSubTaskId,
//         },
//       };
//     }
//     nameMap.set(normalized, where);
//   }

//   return { isValid: true };
// }
export async function validateUniqueTaskNames(tasks = []) {
  const taskIdSet = new Set();
  const outcomeIdSet = new Set();
  let errorMsg;
  let errorTaskId = [];
  let errorSubTaskId = [];

  // Validate task IDs uniqueness
  for (const task of tasks) {
    if (task.id == null) {
      errorMsg = `Task with null ID found`;
      return {
        isValid: false,
        message: errorMsg,
        details: {
          errorTaskId: errorTaskId,
          errorSubTaskId: errorSubTaskId,
        },
      };
    }

    // Validate every task must have at least one outcome
    // if (!task.outcomes || task.outcomes.length === 0) {
    //   errorMsg = `Task '${task.name}' must have at least one outcome`;
    //   errorTaskId.push(task.id);
    //   return {
    //     isValid: false,
    //     message: errorMsg,
    //     details: {
    //       errorTaskId: errorTaskId,
    //       errorSubTaskId: errorSubTaskId,
    //     },
    //   };
    // }

    
    //  task must have atleast one outcome/subTask
    if (task?.subTasks.length === 0 && task?.outcomes?.length === 0) {
      errorMsg = `Task '${task.name}' must have at least one subtask or outcome`;
      errorTaskId.push(task.id);
      return {
        isValid: false,
        message: errorMsg,
        details: {
          errorTaskId: errorTaskId,
          errorSubTaskId: errorSubTaskId,
        },
      };
    }

    if (taskIdSet.has(task.id)) {
      errorMsg = `Duplicate task ID '${task.name}' found`;
      errorTaskId.push(task.id);
      return {
        isValid: false,
        message: errorMsg,
        details: {
          errorTaskId: errorTaskId,
          errorSubTaskId: errorSubTaskId,
        },
      };
    }

    taskIdSet.add(task.id);

    // Validate subtask IDs uniqueness
    if (task.subTasks?.length) {
      for (const subTask of task.subTasks) {
        if (subTask.id == null) {
          errorMsg = `Subtask with null ID found in task '${task.name}'`;
          return {
            isValid: false,
            message: errorMsg,
            details: {
              errorTaskId: errorTaskId,
              errorSubTaskId: errorSubTaskId,
            },
          };
        }

        // Validate every subtask must have at least one outcome
        if (!subTask.outcomes || subTask.outcomes.length === 0) {
          errorMsg = `Subtask '${subTask.name}' must have at least one outcome`;
          errorTaskId.push(task.id);
          errorSubTaskId.push({
            parentId: task.id,
            subtaskId: subTask.id,
          });
          return {
            isValid: false,
            message: errorMsg,
            details: {
              errorTaskId: errorTaskId,
              errorSubTaskId: errorSubTaskId,
            },
          };
        }

        if (taskIdSet.has(subTask.id)) {
          errorMsg = `Duplicate subtask ID '${subTask.name}' found`;
          errorTaskId.push(task.id);
          errorSubTaskId.push({
            parentId: task.id,
            subtaskId: subTask.id,
          });
          return {
            isValid: false,
            message: errorMsg,
            details: {
              errorTaskId: errorTaskId,
              errorSubTaskId: errorSubTaskId,
            },
          };
        }

        taskIdSet.add(subTask.id);
      }
    }
  }

  // Validate outcome IDs uniqueness within each task and subtask separately
  for (const task of tasks) {
    // Create a separate set for this task's outcomes
    const taskOutcomeIdSet = new Set();

    // Validate task outcomes uniqueness
    if (task.outcomes?.length) {
      for (const outcome of task.outcomes) {
        if (outcome.id != null) {
          if (taskOutcomeIdSet.has(outcome.id)) {
            errorMsg = `Duplicate outcome ID '${outcome.id}' found in task '${task.id}'`;
            errorTaskId.push(task.id);
            return {
              isValid: false,
              message: errorMsg,
              details: {
                errorTaskId: errorTaskId,
                errorSubTaskId: errorSubTaskId,
              },
            };
          }
          taskOutcomeIdSet.add(outcome.id);
        }
      }
    }

    // Validate subtask outcomes uniqueness within each subtask
    if (task.subTasks?.length) {
      for (const subTask of task.subTasks) {
        // Create a separate set for this subtask's outcomes
        const subTaskOutcomeIdSet = new Set();

        if (subTask.outcomes?.length) {
          for (const outcome of subTask.outcomes) {
            if (outcome.id != null) {
              if (subTaskOutcomeIdSet.has(outcome.id)) {
                errorMsg = `Duplicate outcome ID '${outcome.id}' found in subtask '${subTask.id}'`;
                errorTaskId.push(task.id);
                errorSubTaskId.push({
                  parentId: task.id,
                  subtaskId: subTask.id,
                });
                return {
                  isValid: false,
                  message: errorMsg,
                  details: {
                    errorTaskId: errorTaskId,
                    errorSubTaskId: errorSubTaskId,
                  },
                };
              }
              subTaskOutcomeIdSet.add(outcome.id);
            }
          }
        }
      }
    }
  }

  // Validate mainTaskId references
  for (const task of tasks) {
    if (task.subTasks?.length) {
      for (const subTask of task.subTasks) {
        if (subTask.mainTaskId != null && subTask.mainTaskId !== task.id) {
          errorMsg = `Invalid mainTaskId reference '${subTask.mainTaskId}' in subtask '${subTask.name}'. Should reference parent task '${task.name} '.`;
          errorTaskId.push(task.id);
          errorSubTaskId.push({
            parentId: task.id,
            subtaskId: subTask.id,
          });
          return {
            isValid: false,
            message: errorMsg,
            details: {
              errorTaskId: errorTaskId,
              errorSubTaskId: errorSubTaskId,
            },
          };
        }
      }
    }
  }

  return { isValid: true };
}

// 11. Validate Multiple Workflow Chains
export async function validateMultipleWorkflowChains(workflows) {
  if (!Array.isArray(workflows) || workflows.length <= 1) {
    return { isDetected: false };
  }

  const taskConnectionMap = new Map();

  // Initialize task map
  workflows.forEach((task) => {
    if (task?.id) taskConnectionMap.set(task.id, new Set());

    task.subTasks?.forEach((subtask) => {
      if (subtask?.id) taskConnectionMap.set(subtask.id, new Set());
    });
  });

  // Build bidirectional connections
  workflows.forEach((task) => {
    if (!task?.id) return;

    // Main task outcomes
    task.outcomes?.forEach((outcome) => {
      outcome?.TriggerTasks?.forEach((triggerId) => {
        if (taskConnectionMap.has(triggerId)) {
          taskConnectionMap.get(task.id).add(triggerId);
          taskConnectionMap.get(triggerId).add(task.id);
        }
      });
    });

    // Subtasks and their outcomes
    task.subTasks?.forEach((subtask) => {
      if (!subtask?.id) return;

      // Link subtask ↔ main task
      taskConnectionMap.get(task.id).add(subtask.id);
      taskConnectionMap.get(subtask.id).add(task.id);

      subtask.outcomes?.forEach((outcome) => {
        outcome?.TriggerTasks?.forEach((triggerId) => {
          if (taskConnectionMap.has(triggerId)) {
            taskConnectionMap.get(subtask.id).add(triggerId);
            taskConnectionMap.get(triggerId).add(subtask.id);
          }
        });
      });
    });
  });

  // Helper to find connected tasks using BFS
  function findConnectedTasks(startId) {
    const visited = new Set();
    const queue = [startId];

    while (queue.length) {
      const current = queue.shift();
      if (!visited.has(current)) {
        visited.add(current);
        const neighbors = taskConnectionMap.get(current);
        neighbors?.forEach((id) => {
          if (!visited.has(id)) queue.push(id);
        });
      }
    }

    return visited;
  }

  // Collect all task IDs
  const allTaskIds = new Set();
  workflows.forEach((wf) => {
    if (wf?.id) allTaskIds.add(wf.id);
    wf.subTasks?.forEach((st) => {
      if (st?.id) allTaskIds.add(st.id);
    });
  });

  // Find connected chains
  const processed = new Set();
  const chains = [];

  allTaskIds.forEach((taskId) => {
    if (!processed.has(taskId)) {
      const connected = findConnectedTasks(taskId);

      const names = Array.from(connected).map((id) => {
        const main = workflows.find((w) => w.id === id);
        if (main) return main.name;

        for (const wf of workflows) {
          const st = wf.subTasks?.find((s) => s.id === id);
          if (st) return st.name;
        }

        return `Task ${id}`;
      });

      chains.push({ taskIds: connected, taskNames: names });
      connected.forEach((id) => processed.add(id));
    }
  });

  if (chains.length > 1) {
    const details = chains.map(
      (c, i) =>
        `Chain ${i + 1} (${c.taskIds.size} tasks): [${c.taskNames.join(", ")}]`
    );

    return {
      isDetected: true,
      message: "Multiple disconnected workflow chains detected",
      details,
    };
  }

  return { isDetected: false };
}
// 12.
export async function safelyAnalyzeWorkflows(inputWorkflows) {
  try {
    const safeWorkflows = (inputWorkflows ?? [])
      .filter(
        (workflow) =>
          workflow &&
          typeof workflow === "object" &&
          typeof workflow.name === "string"
      )
      .map((wf) => ({
        ...wf,
        id: wf?.id ?? null,
        name: wf?.name ?? "",
        outcomes: Array.isArray(wf?.outcomes) ? wf.outcomes : [],
        subTasks: Array.isArray(wf?.subTasks) ? wf.subTasks : [],
      }));

    const results = await executeWorkflowAnalysis(safeWorkflows);
    return results;
  } catch (error) {
    console.error("Safe workflow analysis failed:", error);
    return [];
  }
}
// 13.
export async function validateWorkflowsForNoCrossProductJump(workflows) {
  const result = {
    isValid: true,
    messages: [],
    errorTaskId: [],
    errorSubTaskId: [],
  };

  try {
    if (!Array.isArray(workflows)) {
      return {
        isValid: false,
        messages: ["Input must be an array of workflows"],
        errorTaskId: [],
        errorSubTaskId: [],
      };
    }

    const taskMap = new Map();

    // Step 1: Build task map and validate subtasks
    workflows.forEach((wf) => {
      if (!wf?.id) return;

      taskMap.set(wf.id, wf);

      const subTasks = wf.subTasks ?? [];

      subTasks.forEach((subtask) => {
        if (!subtask) return;

        const isInvalidSubtask =
          wf.isGeneric === false &&
          wf.productId != null &&
          !subtask.isGeneric &&
          subtask.productId !== wf.productId;

        if (isInvalidSubtask) {
          result.isValid = false;
          result.errorTaskId.push(wf.id);
          result.errorSubTaskId.push({
            parentId: wf.id,
            subtaskId: subtask.id,
          });
          result.messages.push(
            `${subtask.name} under ${wf.name} are linked to different products and cannot be processed together:`
          );
        }

        if (subtask?.id != null) {
          taskMap.set(subtask.id, subtask);
        }
      });
    });

    // Step 2: Validate TriggerTasks for cross-product violations
    taskMap.forEach((task) => {
      const taskProductId = task.productId;
      const taskIsGeneric = task.isGeneric ?? false;
      const taskName = task.name ?? "Unknown Task";
      const taskId = task.id ?? "Unknown ID";

      (task.outcomes ?? []).forEach((outcome) => {
        (outcome.TriggerTasks ?? []).forEach((triggerId) => {
          const target = taskMap.get(triggerId);

          if (!target) {
            result.isValid = false;
            result.messages.push(
              `Task "${taskName}" (id=${taskId}) triggers non-existent task id=${triggerId}.`
            );
            if (task.id != null) result.errorTaskId.push(task.id);
            return;
          }

          const targetProductId = target.productId;
          const targetIsGeneric = target.isGeneric ?? false;

          const isCrossProductConflict =
            !taskIsGeneric &&
            !targetIsGeneric &&
            taskProductId !== null &&
            targetProductId !== null &&
            taskProductId !== targetProductId;

          if (isCrossProductConflict) {
            result.isValid = false;
            result.messages.push(
              "Looks like the predecessor and successor tasks are linked to different products. Please align them to the same product or use a generic task if needed"
            );

            if (task.isSubTask === false) {
              result.errorTaskId.push(task.id);
            }

            if (target.isSubTask === true) {
              result.errorSubTaskId.push({
                parentId: target.mainTaskId,
                subtaskId: target.id,
              });
            } else {
              result.errorTaskId.push(target.id);
            }
          }
        });
      });
    });

    // Step 3: Add success message if nothing failed
    if (result.isValid) {
      result.messages.push("All workflow validations passed successfully.");
    }

    return result;
  } catch (error) {
    return {
      isValid: false,
      messages: [
        `Unexpected error during validation: ${
          error?.message ?? "Unknown error"
        }`,
      ],
      errorTaskId: [],
      errorSubTaskId: [],
    };
  }
}
